import React, { useState, useEffect } from 'react';
import { FaHeart, FaShoppingCart, FaTrash, FaEye } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import mainService from '../services/mainService';
import config from '../services/config.json';
import useScrollToTop from '../hooks/useScrollToTop';
import { useToast } from '../contexts/ToastContext';
import Toast from '../components/Toast';
import './Wishlist.css';

const Wishlist = () => {
  const { showToast } = useToast();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddToCartToast, setShowAddToCartToast] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();

  // Scroll to top when wishlist items change
  useScrollToTop([wishlistItems.length]);

  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        setLoading(true);
        setError(null);

        // Support both userData (set by Login) and tokenInfo (used elsewhere)
        const userDataRaw = localStorage.getItem('userData');
        const tokenInfoRaw = localStorage.getItem('tokenInfo');
        const user = userDataRaw ? JSON.parse(userDataRaw) : (tokenInfoRaw ? JSON.parse(tokenInfoRaw) : null);
        if (!user) {
          setWishlistItems([]);
          setLoading(false);
          setError('Please login to view your wishlist');
          return;
        }

        const customerId = user.CustomerId ?? user.Id ?? user.customerId ?? user.id;
        if (customerId == null || customerId === '') {
          setWishlistItems([]);
          setLoading(false);
          setError('Customer ID not found. Please login again.');
          return;
        }

        // Fetch from API and normalize response (API may return array or wrapped object)
        let serverItems = [];
        try {
          const data = await mainService.GetWishlistByCusId(customerId);
          if (Array.isArray(data)) {
            serverItems = data;
          } else if (data && typeof data === 'object') {
            serverItems = Array.isArray(data.data) ? data.data
              : Array.isArray(data.Data) ? data.Data
              : Array.isArray(data.WishlistProducts) ? data.WishlistProducts
              : Array.isArray(data.wishlist) ? data.wishlist
              : Array.isArray(data.Items) ? data.Items
              : Array.isArray(data.items) ? data.items
              : [];
          }
        } catch (apiErr) {
          console.warn('Wishlist API failed, using local only:', apiErr);
          // Continue with local items only
        }

        // Local stored items (client-side additions) - customer-specific storage
        const customerSpecificKey = `wishlistProducts_${customerId}`;
        const stored = localStorage.getItem(customerSpecificKey);
        let localItems = [];
        try {
          const parsed = stored ? JSON.parse(stored) : [];
          localItems = Array.isArray(parsed) ? parsed : [];
        } catch (_) {
          localItems = [];
        }

        // Normalize items that may have nested Product
        const toDisplayString = (val) => {
          if (val == null) return '';
          if (typeof val === 'string') return val;
          if (typeof val === 'object' && (val.Name ?? val.name) != null) return String(val.Name ?? val.name);
          return String(val);
        };
        const normalizeItem = (it) => {
          const product = it.Product ?? it.product ?? it;
          const id = String(it.Id ?? it.id ?? it.ProductId ?? it.productId ?? product.Id ?? product.id ?? product.ProductId ?? '');
          if (!id) return null;
          const rawCategory = it.Category ?? it.category ?? it.CategoryName ?? product.Category ?? product.Code ?? product.code ?? '';
          return {
            Id: it.Id ?? it.id ?? it.ProductId ?? it.productId ?? product.Id ?? product.id,
            Name: toDisplayString(it.Name ?? it.name ?? it.ProductName ?? product.Name ?? product.name ?? 'Product'),
            Price: it.Price ?? it.price ?? product.Price ?? product.price ?? 0,
            OfferPrice: it.OfferPrice ?? it.offerPrice ?? product.OfferPrice ?? product.offerPrice ?? null,
            Image: it.Image ?? it.Image1 ?? it.image ?? product.Image ?? product.Image1 ?? product.image ?? '',
            Category: toDisplayString(rawCategory),
            Rating: it.Rating ?? it.AvgRating ?? product.Rating ?? product.AvgRating ?? product.rating ?? null
          };
        };

        const mapById = new Map();
        [...localItems, ...serverItems].forEach(it => {
          const item = normalizeItem(it);
          if (item) mapById.set(String(item.Id), item);
        });
        setWishlistItems(Array.from(mapById.values()));
      } catch (err) {
        console.error('Failed to load wishlist:', err);
        setWishlistItems([]);
        setError('Failed to load wishlist. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchWishlist();
  }, [retryCount]);

  const removeFromWishlist = async (productId) => {
    try {
      // Get user data to check if logged in
      const userData = localStorage.getItem('userData');
      if (userData) {
        const user = JSON.parse(userData);
        const customerId = user.CustomerId || user.Id;
        
        if (customerId) {
          // Call RemoveWishlist API for logged-in users
          try {
            await mainService.RemoveWishlist(customerId);
            console.log('Removed from wishlist via API:', productId);
          } catch (apiError) {
            console.error('API removal failed, using local removal:', apiError);
          }

          // Sync localStorage with customer-specific keys
          const customerSpecificKey = `wishlistProducts_${customerId}`;
          const stored = localStorage.getItem(customerSpecificKey);
          const parsed = stored ? JSON.parse(stored) : [];
          const next = Array.isArray(parsed) ? parsed.filter(it => String(it.Id) !== String(productId)) : [];
          localStorage.setItem(customerSpecificKey, JSON.stringify(next));

          const savedIdsKey = `wishlistItems_${customerId}`;
          const savedIds = localStorage.getItem(savedIdsKey);
          const ids = savedIds ? new Set(JSON.parse(savedIds)) : new Set();
          ids.delete(productId);
          localStorage.setItem(savedIdsKey, JSON.stringify([...ids]));
        }
      }

      // Remove from local state
      setWishlistItems(prev => prev.filter(item => String(item.Id) !== String(productId)));
      
      // Refresh wishlist count in header
      if (window.refreshWishlistCount) {
        window.refreshWishlistCount();
      }
      
    } catch (error) {
      console.error('Failed to remove from wishlist:', error);
      showToast('Failed to remove item from wishlist', 'error');
    }
  };

  const addToCart = async (productId) => {
    try {
      // TODO: Implement add to cart API call
      console.log('Adding product to cart:', productId);
      setShowAddToCartToast(true);
    } catch (error) {
      console.error('Failed to add to cart:', error);
      showToast('Failed to add product to cart', 'error');
    }
  };

  const viewProduct = (productId, productName) => {
    const encodedName = encodeURIComponent(productName);
    navigate(`/product/${productId}/${encodedName}`);
  };

  if (loading) {
    return (
      <div className="wishlist-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading your wishlist...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wishlist-page">
        <div className="container">
          <div className="error-container">
            <FaHeart className="error-icon" />
            <h3>Error Loading Wishlist</h3>
            <p>{error}</p>
            {error.includes('login') ? (
              <Link to="/login" className="btn btn-primary">Login Now</Link>
            ) : (
              <button type="button" className="btn btn-primary" onClick={() => { setError(null); setRetryCount(c => c + 1); }}>Try again</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wishlist-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">My Wishlist</h1>
          <p className="page-subtitle">
            {wishlistItems.length > 0 
              ? `You have ${wishlistItems.length} item${wishlistItems.length > 1 ? 's' : ''} in your wishlist`
              : 'Your wishlist is empty'
            }
          </p>
        </div>

        <div className="wishlist-content">
          {wishlistItems.length > 0 ? (
            <div className="wishlist-grid">
              {wishlistItems.map((item) => (
                <div key={item.Id} className="wishlist-item">
                  <div className="item-image">
                    <img 
                      src={(item.Image || item.Image1) ? ((item.Image || item.Image1).startsWith('http') ? (item.Image || item.Image1) : config.apiUrl + (item.Image || item.Image1)) : '/images/placeholder-product.jpg'} 
                      alt={item.Name}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  
                  <div className="item-details">
                    <h3 className="item-name">{item.Name}</h3>
                    <p className="item-category">{item.Category || item.CategoryName || ''}</p>
                    
                    <div className="item-price">
                      {item.OfferPrice && Number(item.OfferPrice) < Number(item.Price) ? (
                        <>
                          <span className="offer-price">AED {(Number(item.OfferPrice) || 0).toFixed(2)}</span>
                          <span className="original-price">AED {(Number(item.Price) || 0).toFixed(2)}</span>
                        </>
                      ) : (
                        <span className="price">AED {(Number(item.Price) || 0).toFixed(2)}</span>
                      )}
                    </div>

                    {Number(item.Rating) > 0 ? (
                      <div className="item-rating">
                        {[...Array(5)].map((_, index) => (
                          <span 
                            key={index} 
                            className={`star ${index < Math.floor(Number(item.Rating)) ? 'filled' : ''}`}
                          >
                            ★
                          </span>
                        ))}
                        <span className="rating-text">({Number(item.Rating)})</span>
                      </div>
                    ) : null}
                  </div>

                  <div className="item-actions">
                    <button 
                      className="action-btn view-btn"
                      onClick={() => viewProduct(item.Id, item.Name)}
                      title="View Product"
                    >
                      <FaEye />
                    </button>
                    <button 
                      className="action-btn cart-btn"
                      onClick={() => addToCart(item.Id)}
                      title="Add to Cart"
                    >
                      <FaShoppingCart />
                    </button>
                    <button 
                      className="action-btn remove-btn"
                      onClick={() => removeFromWishlist(item.Id)}
                      title="Remove from Wishlist"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-wishlist">
              <FaHeart className="empty-icon" />
              <h3>Your wishlist is empty</h3>
              <p>Start adding products to your wishlist to see them here</p>
              <Link to="/products" className="btn btn-primary">Browse Products</Link>
            </div>
          )}
        </div>
      </div>

      <Toast
        visible={showAddToCartToast}
        message="Product added to cart!"
        onClose={() => setShowAddToCartToast(false)}
        actionLabel="View cart"
        actionTo="/cart"
      />
    </div>
  );
};

export default Wishlist;
